import React, { useState } from 'react';
import { Shield, AlertTriangle, Zap, FileText, Target, Globe, CheckCircle, Clock, ChevronDown, ChevronUp, Search, Ban } from 'lucide-react';
import RegistrationModal from '../../components/RegistrationModal';
import SEO from '../../components/SEO';
import ServicePricing from '../../components/ServicePricing';
import CTASection from '../../components/CTASection';

const DMCAPage = () => {
  const [openFAQ, setOpenFAQ] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const [modalMode, setModalMode] = useState(null);

  const toggleFAQ = (index) => {
    setOpenFAQ(openFAQ === index ? null : index);
  };

  const openModal = (mode) => {
    setModalMode(mode);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setModalMode(null);
  };

  const benefits = [
    {
      icon: Zap,
      title: "Rapid Removal",
      description: "Most infringing content removed within 24-48 hours through properly formatted DMCA notices."
    },
    {
      icon: Globe,
      title: "Multi-Platform Coverage",
      description: "File takedowns with Google, social media, hosting providers, and e-commerce platforms simultaneously."
    },
    {
      icon: FileText,
      title: "Legal Compliance",
      description: "Attorney-drafted notices meeting all DMCA statutory requirements for maximum effectiveness."
    },
    {
      icon: Shield,
      title: "Safe Harbor Protection",
      description: "Properly executed takedowns protect platforms while removing infringing content swiftly."
    },
    {
      icon: Ban,
      title: "Repeat Infringer Policy",
      description: "Track serial infringers and escalate to account termination for persistent violations."
    },
    {
      icon: Target,
      title: "Counter-Notice Management",
      description: "Professional response to counter-notices with litigation readiness if necessary."
    }
  ];

  const platforms = [
    {
      title: "Search Engines",
      description: "Remove infringing content from search results",
      examples: ["Google Search", "Bing", "Yahoo", "DuckDuckGo"]
    },
    {
      title: "Social Media",
      description: "Takedown notices for unauthorized posts and shares",
      examples: ["Facebook", "Instagram", "Twitter/X", "TikTok", "Pinterest", "LinkedIn"]
    },
    {
      title: "E-Commerce",
      description: "Remove counterfeit products and unauthorized sales",
      examples: ["Amazon", "eBay", "Etsy", "Shopify stores"]
    },
    {
      title: "File Sharing & Hosting",
      description: "Target piracy on hosting and download sites",
      examples: ["Dropbox", "Google Drive", "Mega", "File-sharing networks"]
    }
  ];

  const process = [
    {
      step: "1",
      title: "Infringement Documentation",
      description: "We identify and thoroughly document all instances of infringement with timestamps, URLs, and evidence preservation.",
      timeline: "24-48 hours"
    },
    {
      step: "2",
      title: "DMCA Notice Preparation",
      description: "Our attorneys draft legally compliant DMCA takedown notices with all required statutory elements.",
      timeline: "1-2 days"
    },
    {
      step: "3",
      title: "Filing & Verification",
      description: "We submit notices to platforms and ISPs, track removal progress, and escalate through legal channels if needed.",
      timeline: "3-14 days"
    }
  ];

  const packages = [
    {
      name: "Single Takedown",
      price: "$149",
      description: "One DMCA notice",
      features: [
        "1 DMCA takedown notice",
        "Single URL/location",
        "Basic documentation",
        "Standard filing",
        "Email confirmation",
        "5-day turnaround"
      ],
      popular: false
    },
    {
      name: "Multi-Platform Package",
      price: "$499",
      description: "Up to 10 takedowns",
      features: [
        "Up to 10 takedown notices",
        "Multiple platforms",
        "Comprehensive documentation",
        "Priority filing",
        "Counter-notice response",
        "24-hour turnaround",
        "30-day monitoring"
      ],
      popular: true
    },
    {
      name: "Ongoing Monitoring",
      price: "$999/mo",
      description: "Unlimited takedowns",
      features: [
        "Unlimited takedown notices",
        "Automated monitoring",
        "Real-time alerts",
        "Immediate filing",
        "Full counter-notice handling",
        "Quarterly reports",
        "Dedicated support"
      ],
      popular: false
    }
  ];

  const faqs = [
    {
      question: "What is a DMCA takedown notice?",
      answer: "A DMCA (Digital Millennium Copyright Act) takedown notice is a legal request to remove infringing content from online platforms. Under 17 U.S.C. § 512(c), online service providers must remove or disable access to infringing material when they receive proper notification. The notice must include: (1) identification of your copyrighted work, (2) identification of the infringing material and its location, (3) your contact information, (4) statement of good faith belief, (5) statement of accuracy under penalty of perjury, and (6) your physical or electronic signature."
    },
    {
      question: "How quickly can content be removed with a DMCA notice?",
      answer: "Most platforms remove content within 24-48 hours of receiving a valid DMCA notice. Some platforms (like Google Search) process takedowns within hours. Social media platforms typically respond within 1-2 business days. Hosting providers vary but usually comply within 48 hours. Speed depends on platform policies and whether the notice is properly formatted. Improperly drafted notices may be rejected or delayed, which is why professional preparation is crucial."
    },
    {
      question: "Can I file DMCA notices myself or do I need an attorney?",
      answer: "You can file DMCA notices yourself—the law doesn't require attorney involvement. However, attorney-drafted notices have significant advantages: (1) proper legal formatting ensuring compliance, (2) stronger evidence documentation, (3) professional language that platforms respect, (4) handling of complex situations like counter-notices, (5) reduced risk of perjury claims from improper notices, and (6) litigation readiness if takedown fails. For occasional takedowns, DIY may work. For businesses or valuable content, professional service is recommended."
    },
    {
      question: "What platforms accept DMCA takedown notices?",
      answer: "Virtually all U.S.-based platforms accept DMCA notices including: search engines (Google, Bing), social media (Facebook, Instagram, Twitter, TikTok), video platforms (YouTube, Vimeo), e-commerce sites (Amazon, eBay, Etsy), hosting providers, file-sharing services, and website hosts. International platforms serving U.S. users also generally comply. Each platform has designated DMCA agents—we maintain current contact information and know each platform's specific requirements and preferences for fastest processing."
    },
    {
      question: "What happens if the infringer files a counter-notice?",
      answer: "If the alleged infringer files a counter-notice claiming the content isn't infringing, the platform must notify you. You then have 10-14 business days to file a lawsuit against the infringer. If you don't file suit, the platform may restore the content. Counter-notices are rare (less than 5% of cases) because they expose the counter-notifier to perjury penalties and potential litigation. We handle counter-notice responses including: assessing merit, communicating with infringer, preparing litigation if necessary, or negotiating settlement. Most counter-notices are frivolous and withdrawn when faced with litigation threat."
    },
    {
      question: "Do I need copyright registration to file a DMCA notice?",
      answer: "No, copyright registration is not required to file DMCA takedown notices. The DMCA protects all copyrighted works regardless of registration status. However, registration provides important benefits: (1) stronger evidence of ownership, (2) ability to sue if counter-notice is filed, (3) statutory damages and attorney's fees in litigation, and (4) greater deterrent effect. For high-value content or repeat infringement, we recommend registration alongside DMCA enforcement. We can expedite registration while processing takedowns."
    },
    {
      question: "Can DMCA notices remove content from search engines?",
      answer: "Yes, DMCA notices can remove infringing content from search engine results. Google, Bing, and other search engines delist pages containing infringing content, making them invisible in search results. This is extremely effective for piracy sites, unauthorized reproductions, and content theft. However, DMCA only removes the search listing—not the actual content from the original site. For complete removal, you need takedowns to both the search engine and the hosting site. We file coordinated notices to search engines and hosts simultaneously for maximum impact."
    },
    {
      question: "What if the infringer is overseas?",
      answer: "DMCA takedowns work against overseas infringers if the content is hosted on platforms subject to U.S. law or serving U.S. users. Most major platforms (Google, Facebook, Amazon) comply with DMCA notices globally. For content on foreign platforms not subject to DMCA, options include: (1) contacting hosting providers directly, (2) filing complaints under foreign copyright laws, (3) using international takedown services, (4) search engine delisting to reduce visibility, or (5) geographic blocking. While enforcement is harder internationally, DMCA remains effective against major platforms regardless of infringer location."
    },
    {
      question: "How much does DMCA takedown service cost?",
      answer: "Costs vary by volume and complexity. Single takedown notices start at $149. Multi-platform packages (up to 10 takedowns) cost $499. For ongoing issues, monthly monitoring with unlimited takedowns runs $999/month. Volume discounts available for large-scale infringement. DIY takedowns are free but risk rejection for improper formatting. Our professional service ensures proper notice formatting, platform-specific requirements, follow-up, and counter-notice handling—worthwhile for valuable content or business-critical enforcement."
    },
    {
      question: "Is there any risk to filing DMCA takedown notices?",
      answer: "The main risk is perjury liability if you knowingly make false statements in the notice (claiming ownership you don't have or identifying non-infringing content). This is why proper documentation is crucial. Other risks include: (1) potential defamation claims for bad faith takedowns (rare and difficult to prove), (2) counter-notice requiring litigation decision, (3) public disclosure of your contact information on some platforms, and (4) possible backlash if takedown is perceived as overreach. These risks are minimal when notices are properly prepared and truthful. We assess each situation to ensure DMCA is the appropriate remedy."
    }
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-white to-gray-50">
      <SEO 
        title="DMCA Takedown Services - Fast Content Removal"
        description="Fast DMCA takedown notices to remove infringing content from websites and social media. 24-48 hour removal."
        keywords="DMCA takedown, copyright takedown, content removal, DMCA notice, online infringement removal"
      />

      {/* Hero Section */}
      <section className="relative text-white py-20 overflow-hidden">
            <img src="https://images.unsplash.com/photo-1550751827-4bd374c3f58b?w=900w=1600&q=80q=50" alt="" loading="eager" fetchpriority="high" className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-br from-[#2F1A8E]/90 via-[#1a0f5e]/85 to-[#0d0830]/90"></div>
            <div className="absolute top-0 right-0 w-96 h-96 bg-[#FF035B]/10 rounded-full blur-3xl"></div>
            <div className="absolute bottom-0 left-0 w-64 h-64 bg-[#FF035B]/5 rounded-full blur-2xl"></div>
        <div className="relative z-10 max-w-7xl mx-auto px-6">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <div>
              <div className="inline-flex items-center gap-2 bg-[#FF035B]/20 text-[#FF035B] px-4 py-2 rounded-full text-sm font-semibold mb-6">
                <Zap className="w-4 h-4" />
                <span>Fast Content Removal</span>
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
                DMCA Takedown Services - Remove Infringing Content Fast
              </h1>
              <p className="text-xl mb-8 text-gray-300">
                Professional DMCA takedown notices to quickly remove unauthorized content from websites, search engines, social media, and online platforms. Most content removed within 24-48 hours.
              </p>
              <div className="flex flex-wrap gap-4">
                <button 
                  onClick={() => openModal('registration')}
                  className="bg-[#FF035B] hover:bg-[#2F1A8E] text-white px-8 py-4 rounded-lg font-semibold transition-all hover:shadow-xl hover:scale-105"
                >
                  File Takedown Notice
                </button>
                <button 
                  onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
                  className="bg-white text-gray-900 px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all"
                >
                  Speak with Expert
                </button>
              </div>
              <div className="mt-8 flex items-center gap-6 text-sm">
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>24-48 Hour Removal</span>
                </div>
                <div className="flex items-center gap-2">
                  <CheckCircle className="w-5 h-5 text-green-400" />
                  <span>All Major Platforms</span>
                </div>
              </div>
            </div>
            <div className="bg-white/10 backdrop-blur-lg rounded-2xl p-8 border border-white/20">
              <h3 className="text-lg font-bold mb-6">Why DMCA Takedowns Work</h3>
              <ul className="space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Legal Obligation</strong>
                    <span className="text-gray-300">Platforms must remove content or lose safe harbor protection</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Fast Process</strong>
                    <span className="text-gray-300">Automated systems prioritize properly formatted notices</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-6 h-6 text-green-400 flex-shrink-0 mt-1" />
                  <div>
                    <strong className="block">Cost-Effective</strong>
                    <span className="text-gray-300">Much cheaper than litigation with similar effectiveness</span>
                  </div>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* Benefits */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Professional DMCA Services
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert takedown notices for rapid content removal
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <div key={index} className="group bg-white hover:bg-gradient-to-br hover:from-[#FF035B] hover:to-[#2F1A8E] rounded-xl p-8 shadow-lg hover:shadow-2xl transition-all duration-500 hover:-translate-y-2 hover:scale-105 cursor-pointer hover:shadow-xl transition-all border border-gray-100">
                <benefit.icon className="w-12 h-12 text-[#FF035B] group-hover:text-white transition-all duration-500 group-hover:scale-110 group-hover:rotate-6 mb-4" />
                <h3 className="text-xl font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{benefit.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 leading-relaxed group-hover:text-white/90 transition-colors duration-500">{benefit.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Platforms */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Platforms We Cover
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              File takedowns across all major online platforms
            </p>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
            {platforms.map((platform, index) => (
              <div key={index} className="bg-white rounded-xl p-8 shadow-md hover:shadow-lg transition-all">
                <h3 className="text-lg font-bold text-gray-900 group-hover:text-white transition-colors duration-500 mb-3">{platform.title}</h3>
                <p className="text-gray-600 group-hover:text-white/90 transition-colors duration-500 mb-6">{platform.description}</p>
                <div className="flex flex-wrap gap-2">
                  {platform.examples.map((example, idx) => (
                    <span key={idx} className="px-3 py-1 bg-[#FF035B]/10 text-[#FF035B] rounded-full text-sm font-medium">
                      {example}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process */}
      <section className="py-20">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              DMCA Takedown Process
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From detection to removal in days
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {process.map((item, index) => (
              <div key={index} className="relative">
                <div className="bg-gradient-to-br from-[#FF035B] to-[#2F1A8E] text-white rounded-xl p-6 shadow-lg">
                  <div className="text-4xl font-bold mb-2 opacity-50">{item.step}</div>
                  <h3 className="text-xl font-bold mb-3">{item.title}</h3>
                  <p className="text-sm mb-4 text-white/90">{item.description}</p>
                  <div className="flex items-center gap-2 text-sm font-semibold">
                    <Clock className="w-4 h-4" />
                    {item.timeline}
                  </div>
                </div>
                {index < process.length - 1 && (
                  <div className="hidden lg:block absolute top-1/2 -right-4 w-8 h-0.5 bg-gradient-to-r from-[#FF035B] to-transparent"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      <ServicePricing showPrices={false} title="DMCA Takedown Services" subtitle="Get a personalized quote for your specific needs" />


      {/* FAQ */}
      <section className="py-20">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Frequently Asked Questions
            </h2>
            <p className="text-xl text-gray-600">
              Everything you need to know about DMCA takedowns
            </p>
          </div>
          <div className="space-y-4">
            {faqs.map((faq, index) => (
              <div key={index} className="bg-white rounded-xl shadow-md overflow-hidden">
                <button
                  onClick={() => toggleFAQ(index)}
                  className="w-full px-8 py-6 text-left flex items-center justify-between hover:bg-gray-50 transition-all"
                >
                  <span className="font-semibold text-gray-900 pr-8">{faq.question}</span>
                  {openFAQ === index ? (
                    <ChevronUp className="w-5 h-5 text-[#FF035B] flex-shrink-0" />
                  ) : (
                    <ChevronDown className="w-5 h-5 text-gray-400 flex-shrink-0" />
                  )}
                </button>
                {openFAQ === index && (
                  <div className="px-8 pb-6">
                    <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-gradient-to-r from-[#FF035B] to-[#2F1A8E] text-white">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-6">
            Stop Infringement Today
          </h2>
          <p className="text-xl mb-8 text-white/90">
            Don't let unauthorized content damage your brand. Fast, professional DMCA takedown services to remove infringing content from any platform.
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <button 
              onClick={() => openModal('registration')}
              className="bg-white text-[#FF035B] px-8 py-4 rounded-lg font-semibold hover:bg-gray-100 transition-all hover:shadow-xl"
            >
              File Takedown Now
            </button>
            <button 
              onClick={() => { if (window.cd360Api && window.cd360Api.liveChat) window.cd360Api.liveChat.showWebWidget(); }}
              className="bg-transparent border-2 border-white text-white px-8 py-4 rounded-lg font-semibold hover:bg-white hover:text-[#FF035B] transition-all"
            >
              Speak with Expert
            </button>
          </div>
        </div>
      </section>

      <CTASection />

      <RegistrationModal 
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        initialType="registration"
      />
    </div>
  );
};

export default DMCAPage;
